<script setup>
import { computed, watch, ref } from "vue";

import { generateReceiptPDF } from "../utils/receiptPdf.js";
import { ensureStripe, createPaymentIntent } from "../utils/stripe.js";
import { currentUser } from "../db/auth.js";
import StripePaymentModal from "./StripePaymentModal.vue";
import CashPaymentModal from "./CashPaymentModal.vue";
import PaymentMethodModal from "./PaymentMethodModal.vue";
import PaymentStatusModal from "./PaymentStatusModal.vue";
import PaymentProcessingModal from "./PaymentProcessingModal.vue";
import {
  cartState,
  cartTotal,
  cartCount,
  incrementItem,
  decrementItem,
  removeFromCart,
  clearCart,
  syncCartFromSupabase,
  finalizeSale,
} from "../store/cart.js";
import { supabase, useSupabase } from "../db/supabaseClient.js";

// Estado de los modales de pago
const showStripeModal = ref(false);
const showCashModal = ref(false);
const showPaymentMethodModal = ref(false);
const showStatusModal = ref(false);
const showProcessingModal = ref(false);
const statusType = ref('success');
const statusTitle = ref('');
const statusMessage = ref('');
const statusDetails = ref({});
const stripeClientSecret = ref(null);
const pendingTotal = ref(0);

const isOpen = computed(() => cartState.open);
const items = computed(() => cartState.items);

function close() {
  cartState.open = false;
}
// Sincronizar al abrir si usamos Supabase
watch(isOpen, async (val) => {
  if (val && useSupabase()) {
    try { await syncCartFromSupabase(); } catch { }
  }
});

async function checkout() {
  const total = Number(cartTotal() || 0);
  if (total <= 0) {
    statusType.value = 'error';
    statusTitle.value = 'Carrito vacío';
    statusMessage.value = 'Agrega productos antes de pagar';
    showStatusModal.value = true;
    return;
  }
  cartState.open = false;
  pendingTotal.value = total;
  showPaymentMethodModal.value = true;
}

function handleSelectCash() {
  showPaymentMethodModal.value = false;
  showCashModal.value = true;
}

async function handleSelectCard() {
  showPaymentMethodModal.value = false;
  try {
    await ensureStripe();
    const amountCents = Math.round(Number(pendingTotal.value || 0) * 100);
    const clientSecret = await createPaymentIntent(amountCents, { source: 'cart' });

    stripeClientSecret.value = clientSecret;
    showStripeModal.value = true;
  } catch (err) {
    statusType.value = 'error';
    statusTitle.value = 'Error';
    statusMessage.value = err?.message || 'No se pudo iniciar el pago con tarjeta';
    showStatusModal.value = true;
  }
}

// Manejar éxito del pago
async function handlePaymentSuccess(result) {
  showStripeModal.value = false;
  showProcessingModal.value = true; // Mostrar carga mientras se finaliza

  try {
    const res = await finalizeSale({ payment_method: "card" });
    if (res?.ok) {
      try {
        const pi = result?.paymentIntent?.id || null;
        const ch = result?.paymentIntent?.latest_charge || (result?.paymentIntent?.charges && Array.isArray(result.paymentIntent.charges.data) ? result.paymentIntent.charges.data[0]?.id : null);
        if (useSupabase() && res.sale_id && (pi || ch)) {
          const payload = {};
          if (pi) payload.payment_intent_id = String(pi);
          if (ch) payload.payment_charge_id = String(ch);
          await supabase
            .from('sales')
            .update(payload)
            .eq('id_sales', res.sale_id);
        }
      } catch { }
      const u = currentUser();
      const customerName = u?.full_name || u?.username || 'Cliente';
      await generateReceiptPDF({
        logoUrl: "/img/sushi.png",
        title: "Sushito",
        saleId: res.sale_id,
        items: res.items || [],
        total: res.total,
        paymentMethod: "card",
        customerName,
      });

      showProcessingModal.value = false; // Ocultar carga
      statusType.value = 'success';
      statusTitle.value = '¡Pago exitoso!';
      statusMessage.value = 'La transacción se completó correctamente.';
      statusDetails.value = { total: res.total };
      showStatusModal.value = true;

      try { window.dispatchEvent(new CustomEvent('ops:sale:created', { detail: { saleId: res.sale_id, total: res.total } })); } catch { }
    }
  } catch (err) {
    showProcessingModal.value = false;
    statusType.value = 'error';
    statusTitle.value = 'Error';
    statusMessage.value = 'No se pudo registrar la venta';
    showStatusModal.value = true;
  }
}

// Manejar error del pago
async function handlePaymentError(message) {
  showStripeModal.value = false;
  statusType.value = 'error';
  statusTitle.value = 'Pago rechazado';
  statusMessage.value = message || 'No se pudo procesar el pago';
  showStatusModal.value = true;
}

// Manejar cierre del modal de tarjeta
function handleModalClose() {
  showStripeModal.value = false;
  stripeClientSecret.value = null;
}

// Manejar confirmación de pago en efectivo
async function handleCashConfirm({ amountReceived, change }) {
  showCashModal.value = false;
  showProcessingModal.value = true; // Mostrar carga

  try {
    const res = await finalizeSale({
      payment_method: "cash",
      amount_received: amountReceived,
      change
    });

    if (res?.ok) {
      const u = currentUser();
      const customerName = u?.full_name || u?.username || 'Cliente';
      await generateReceiptPDF({
        logoUrl: "/img/sushi.png",
        title: "Sushito",
        saleId: res.sale_id,
        items: res.items || [],
        total: res.total,
        paymentMethod: "cash",
        amountReceived,
        change,
        customerName,
      });

      showProcessingModal.value = false; // Ocultar carga
      statusType.value = 'success';
      statusTitle.value = '¡Venta realizada!';
      statusMessage.value = 'Pago en efectivo registrado correctamente.';
      statusDetails.value = { total: res.total, change: change };
      showStatusModal.value = true;

      try { window.dispatchEvent(new CustomEvent('ops:sale:created', { detail: { saleId: res.sale_id, total: res.total } })); } catch { }
    } else {
      showProcessingModal.value = false;
      statusType.value = 'error';
      statusTitle.value = 'Error';
      statusMessage.value = res?.message || "No se pudo registrar la venta";
      showStatusModal.value = true;
    }
  } catch (err) {
    showProcessingModal.value = false;
    statusType.value = 'error';
    statusTitle.value = 'Error';
    statusMessage.value = 'No se pudo procesar la venta';
    showStatusModal.value = true;
  }
}

// Manejar cancelación de pago en efectivo
function handleCashCancel() {
  showCashModal.value = false;
}
</script>

<template>
  <div v-if="isOpen" class="overlay" @click="close" aria-hidden="true"></div>
  <aside v-if="isOpen" class="sidebar" aria-label="Carrito de compras">
    <header class="bar-top">
      <h2 class="title">Mi pedido</h2>
      <button class="icon-btn" @click="close" aria-label="Cerrar">
        <span class="material-symbols-rounded">close</span>
      </button>
    </header>
    <section class="content">
      <div v-if="!items.length" class="empty">Tu carrito está vacío</div>
      <ul v-else class="list">
        <li v-for="it in items" :key="it.id" class="row">
          <div class="thumb" v-if="it.image_base64">
            <img :src="`data:image/jpeg;base64,${it.image_base64}`" alt="imagen" />
          </div>
          <div class="info">
            <div class="name">{{ it.name }}</div>
            <div class="price">${{ it.unit_price.toFixed(2) }}</div>
          </div>
          <div class="qty">
            <button class="qty-btn" @click="decrementItem(it.id)" aria-label="Menos">
              <span class="material-symbols-rounded">remove</span>
            </button>
            <span class="qty-val">{{ it.qty }}</span>
            <button class="qty-btn" @click="incrementItem(it.id)" aria-label="Más">
              <span class="material-symbols-rounded">add</span>
            </button>
          </div>
          <div class="total">${{ (it.qty * it.unit_price).toFixed(2) }}</div>
          <button class="del" @click="removeFromCart(it.id)" title="Eliminar">
            <span class="material-symbols-rounded">delete</span>
          </button>
        </li>
      </ul>
    </section>
    <footer class="bar-bottom">
      <div class="summary">
        <span class="count">{{ cartCount() }} items</span>
        <span class="sum">Total: ${{ cartTotal().toFixed(2) }}</span>
      </div>
      <div class="actions">
        <button class="btn ghost" @click="clearCart">
          <span class="material-symbols-rounded" aria-hidden="true">delete</span>
          Vaciar
        </button>
        <button class="btn primary" @click="checkout">
          <span class="material-symbols-rounded" aria-hidden="true">payment</span>
          Pagar
        </button>
      </div>
    </footer>
  </aside>

  <!-- Modal de selección de método de pago -->
  <PaymentMethodModal :show="showPaymentMethodModal" @close="showPaymentMethodModal = false"
    @select-cash="handleSelectCash" @select-card="handleSelectCard" />

  <!-- Modal de pago con tarjeta -->
  <StripePaymentModal :show="showStripeModal" :total="pendingTotal" :clientSecret="stripeClientSecret"
    @close="handleModalClose" @success="handlePaymentSuccess" @error="handlePaymentError" />

  <!-- Modal de pago en efectivo -->
  <CashPaymentModal :show="showCashModal" :total="pendingTotal" @confirm="handleCashConfirm"
    @cancel="handleCashCancel" />

  <!-- Modal de estado de pago (Éxito/Error) -->
  <PaymentStatusModal :show="showStatusModal" :type="statusType" :title="statusTitle" :message="statusMessage"
    :details="statusDetails" @close="showStatusModal = false" />

  <!-- Modal de carga -->
  <PaymentProcessingModal :show="showProcessingModal" />
</template>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 9999;
  /* por encima del topbar */
}

.sidebar {
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 360px;
  background: var(--panel-bg);
  border-left: 1px solid var(--panel-border);
  display: grid;
  grid-template-rows: auto 1fr auto;
  z-index: 10000;
  /* asegúrate de estar al frente */
  overflow: hidden;
  /* evita que el scroll tape header/footer */
  box-sizing: border-box;
  padding-right: 8px;
  /* margen de seguridad contra la barra del sistema */
}

.bar-top,
.bar-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
}

.bar-bottom {
  flex-wrap: wrap;
  gap: 0.5rem;
}

.title {
  font-weight: 700;
}

.icon-btn {
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 10px;
  padding: 0.35rem;
  color: var(--text);
}

.content {
  padding: 0.5rem;
  padding-bottom: 1rem;
  /* evita que el footer tape contenido */
  overflow-y: auto;
  /* el desplazamiento ocurre dentro del contenido */
  scrollbar-gutter: stable;
  /* reserva espacio para la scrollbar */
}

.empty {
  color: var(--muted);
  padding: 1rem;
  text-align: center;
}

.list {
  display: grid;
  gap: 0.5rem;
}

.row {
  display: grid;
  grid-template-columns: 48px 1fr auto auto auto;
  align-items: center;
  gap: 0.5rem;
  border-bottom: 1px dashed var(--border);
  padding-bottom: 0.4rem;
}

.thumb img {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 6px;
}

.name {
  font-weight: 600;
}

.price {
  color: var(--muted);
  font-size: 0.9rem;
}

.qty {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.qty-btn {
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 8px;
  color: var(--text);
  padding: 0.25rem;
}

.qty-val {
  min-width: 24px;
  text-align: center;
}

.total {
  font-weight: 600;
}

.del {
  border: none;
  background: transparent;
  color: var(--muted);
}

.summary {
  display: inline-flex;
  gap: 1rem;
}

.btn {
  border-radius: 10px;
  padding: 0.5rem 0.8rem;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
}

.btn.ghost {
  background: var(--surface);
  border: 1px solid var(--border);
  color: var(--text);
}

.btn.primary {
  background: #2563eb;
  /* azul para botón de pagar */
  color: white;
  border: none;
}

.btn .material-symbols-rounded {
  font-size: 18px;
}

/* Mejor distribución del footer en pantallas estrechas */
.actions {
  display: inline-flex;
  gap: 0.5rem;
}

/* Responsivo */
@media (max-width: 640px) {
  .sidebar {
    width: min(92vw, 420px);
  }

  .row {
    grid-template-columns: 40px 1fr auto;
  }

  .total,
  .del {
    display: none;
  }

  .bar-bottom {
    flex-direction: column;
    align-items: stretch;
    gap: 0.6rem;
  }

  .summary {
    display: flex;
    justify-content: space-between;
  }

  .actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
}
</style>
